import { apiFetch } from './client';

/**
 * Ruft einen frischen, zeitstempel-gesicherten und kryptographisch signierten
 * QR-Pass für den eingeloggten Abenteurer ab.
 */
export async function getAdventurerQrPass() {
  return await apiFetch('/users/qr-pass', { cache: 'no-store' });
}

export async function getAdventurerRedemptions() {
  return await apiFetch('/users/partner-redemptions');
}

export function getAvailablePartnerOffers() {
  return apiFetch('/partner/offers/available', { cache: 'no-store' });
}

/**
 * Ruft die öffentlichen Partnerdetails und aktiven Angebote für Abenteurer ab.
 */
export async function getPublicPartnerDetails(partnerId) {
  return await apiFetch(`/partner/${partnerId}`);
}

/**
 * Partner scannt & verifiziert einen Abenteurer QR-Code Token.
 */
export async function verifyAdventurerQr(qrToken) {
  return await apiFetch('/partner/scan-qr', {
    method: 'POST',
    body: JSON.stringify({ qrToken }),
  });
}

/**
 * Partner löst ein Angebot für den Abenteurer vor Ort ein.
 */
export async function redeemPartnerOffer(offerId, adventurerId, qrToken, notes = '') {
  return await apiFetch('/partner/redeem', {
    method: 'POST',
    body: JSON.stringify({ offerId, adventurerId, qrToken, notes }),
  });
}

/**
 * Abenteurer löst ein digitales Angebot direkt online ein (Gutscheincode, Rabatt, URL).
 */
export async function redeemDigitalOffer(offerId) {
  return await apiFetch(`/partner/offers/${offerId}/redeem-digital`, {
    method: 'POST',
  });
}

/**
 * Partner-Angebote abrufen.
 */
export async function getPartnerOffers(adminUserId = null) {
  const url = adminUserId ? `/admin/users/${adminUserId}/partner-offers` : '/partner/offers';
  return await apiFetch(url);
}

export function getPartnerOfferAudience(requirements) {
  return apiFetch('/partner/offers/audience', { method: 'POST', body: JSON.stringify(requirements) });
}

/**
 * Partner-Einlösungshistorie abrufen (Vor Ort & Digital).
 */
export async function getPartnerRedemptions(adminUserId = null, page = 1, search = '', mode = 'ALL', pageSize = 30) {
  const url = adminUserId ? `/admin/users/${adminUserId}/partner-redemptions` : '/partner/redemptions';
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize), search, mode });
  return await apiFetch(`${url}?${params}`);
}

export async function cancelPartnerRedemption(id, reason) {
  return apiFetch(`/partner/redemptions/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) });
}

export async function getPartnerAnalytics() {
  return apiFetch('/partner/analytics');
}

/**
 * Neues Partner-Angebot anlegen.
 */
export async function createPartnerOffer(dto, adminUserId = null) {
  const url = adminUserId ? `/admin/users/${adminUserId}/partner-offers` : '/partner/offers';
  return await apiFetch(url, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export async function updatePartnerOffer(offerId, dto, adminUserId = null) {
  const url = adminUserId ? `/admin/users/${adminUserId}/partner-offers/${offerId}` : `/partner/offers/${offerId}`;
  return await apiFetch(url, {
    method: 'PUT',
    body: JSON.stringify(dto),
  });
}

/**
 * Partner-Angebot löschen.
 */
export async function deletePartnerOffer(offerId, adminUserId = null) {
  const url = adminUserId ? `/admin/users/${adminUserId}/partner-offers/${offerId}` : `/partner/offers/${offerId}`;
  return await apiFetch(url, {
    method: 'DELETE',
  });
}

export async function setPartnerOfferActive(offerId, isActive) {
  // The existing DELETE endpoint soft-deactivates an offer and is also available
  // while an older development server is still running.
  if (!isActive) return deletePartnerOffer(offerId);
  return apiFetch(`/partner/offers/${offerId}/active`, {
    method: 'PATCH',
    body: JSON.stringify({ isActive }),
  });
}

/**
 * Partner-Profil abrufen.
 */
export async function getPartnerProfile(adminUserId = null) {
  const url = adminUserId ? `/admin/users/${adminUserId}/partner-profile` : '/partner/profile';
  return await apiFetch(url);
}

/**
 * Partner-Profil aktualisieren.
 */
export async function updatePartnerProfile(dto, adminUserId = null) {
  const url = adminUserId ? `/admin/users/${adminUserId}/partner-profile` : '/partner/profile';
  return await apiFetch(url, {
    method: 'PUT',
    body: JSON.stringify(dto),
  });
}
